import Vue from 'vue';
import Router from 'vue-router';
import Home from '@/components/home/index.vue';
import Login from '@/components/login/index.vue';
import Reset from '@/components/login/reset.vue';
import Confirm from '@/components/login/confirm.vue';
import Schedule from '@/components/schedule/index.vue';
import MixingConsole from '@/components/schedule/MixingConsole.vue';
import LayoutEditor from '@/components/design/layoutEditor/index.vue';
import Boxes from '@/components/boxes/Boxes.vue';
import BoxGroups from '@/components/groups/BoxGroups.vue';
import Media from '@/components/library/Media.vue';
import UserManagement from '@/components/administration/UserTable.vue';
import OrganizationManagement from '@/components/administration/OrganizationTable.vue';
import BoxManagement from '@/components/administration/BoxTable.vue';
import NewsEditor from '@/components/administration/NewsEditor.vue';
import SpecialWidgets from '@/components/administration/SpecialWidgets.vue';
import UpdateManagement from '@/components/administration/UpdateManagement.vue';
import Layouts from '@/components/design/layoutManagement/LayoutTable.vue';
import Playlists from '@/components/library/Playlists.vue';
import Settings from '@/components/management/Settings.vue';
import OrganizationUserManagement from '@/components/management/UserTable.vue';
import Developer from '@/components/management/Developer.vue';
import store from '@/store';
import loadLanguageAsync from '@/main';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'Login',
      component: Login,
    },
    {
      path: '/password/reset/:token',
      name: 'Reset',
      component: Reset,
    },
    {
      path: '/register/confirm/:confirmationCode',
      name: 'Confirm',
      component: Confirm,
    },
    {
      path: '/home',
      name: 'Home',
      component: Home,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/schedule',
      name: 'Schedule',
      component: Schedule,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/layouts',
      name: 'Layouts',
      component: Layouts,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/layout-editor/:id',
      name: 'Layout Editor',
      component: LayoutEditor,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/boxes',
      name: 'Boxes',
      component: Boxes,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/box-groups',
      name: 'Box Groups',
      component: BoxGroups,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/media',
      name: 'Media',
      component: Media,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/playlists',
      name: 'Playlists',
      component: Playlists,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/mixing-console',
      name: 'Mixing Console',
      component: MixingConsole,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/settings',
      name: 'Settings',
      component: Settings,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/organization-user-management',
      name: 'Organization User Management',
      component: OrganizationUserManagement,
      meta: {
        requiresAuth: true,
        requiresOrganizationAdmin: true,
      },
    },
    {
      path: '/user-management',
      name: 'User Management',
      component: UserManagement,
      meta: {
        requiresAuth: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/organization-management',
      name: 'Organization Management',
      component: OrganizationManagement,
      meta: {
        requiresAuth: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/box-management',
      name: 'Box Management',
      component: BoxManagement,
      meta: {
        requiresAuth: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/news-editor',
      name: 'News editor',
      component: NewsEditor,
      meta: {
        requiresAuth: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/special-widgets',
      name: 'Special widgets',
      component: SpecialWidgets,
      meta: {
        requiresAuth: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/update-management',
      name: 'UpdateManagement',
      component: UpdateManagement,
      meta: {
        requiresAuth: true,
        requiresSuperAdmin: true,
      },
    },
    {
      path: '/developer',
      name: 'Developer',
      component: Developer,
      meta: {
        requiresAuth: true,
        requiresSuperAdmin: true,
      },
    },
  ],
});

router.beforeEach((to, from, next) => {
  /* Check authentication */
  if (
    to.matched.some((record) => record.meta.requiresAuth)
    && !store.getters.isAuthenticated
  ) {
    next({
      path: '/',
      query: { redirect: to.fullPath },
    });

    return;
  }

  /* Redirect to home if already authenticated and tries to access login page */
  if (to.path === '/' && store.getters.isAuthenticated) {
    next({
      path: '/home',
    });

    return;
  }

  /* Check user role */
  if ((
    to.matched.some((record) => record.meta.requiresSuperAdmin)
    && !store.getters.hasRole('superadmin')
  ) || (
    to.matched.some((record) => record.meta.requiresOrganizationAdmin)
    && !store.getters.hasRole('organization_admin')
  )) {
    next({
      path: '/home',
    });

    return;
  }

  /* Check user permissions */
  if (
    (to.path === '/schedule' && !store.getters.hasPermission('view_organization_schedule'))
    || (to.path === '/layouts' && !store.getters.hasPermission('view_organization_layouts'))
    || (to.path === `/layout-editor/${to.params.id}` && !store.getters.hasPermission('edit_organization_layouts'))
    || (to.path === '/boxes' && !store.getters.hasPermission('view_organization_boxes'))
    || (to.path === '/box-groups' && !store.getters.hasPermission('view_organization_boxes'))
    || (to.path === '/media' && !store.getters.hasPermission('view_organization_media'))
    || (to.path === '/playlists' && !store.getters.hasPermission('view_organization_playlists'))
  ) {
    next({
      path: '/home',
    });

    return;
  }

  /* Load correct language */
  const lang = localStorage.getItem('locale');
  loadLanguageAsync(lang).then(() => next());

  /* After all checks, finally send user to desired page */
  next();
});

export default router;
